<template>
  <table :id="id" :class="tableClass" class="ips table w-auto mw-100">
    <thead>
      <tr>
        <th v-if="selectable" scope="col">
          <div>
            <ips-checkbox
              v-if="selectAll && selectType === 'checkbox'"
              :id="id.concat('toggleSelectAll')"
              ref="selectAll"
              label="Select all"
              :checked="status"
              :indeterminate="indeterminate"
              type="checkbox"
              control="checkbox"
              class="ms-1"
              @change="toggleSelectAll"
            />
          </div>
        </th>
        <th
          v-for="column in columns"
          :key="column.key"
          :class="columnHeaderClasses(column)"
          scope="col"
        >
          <ips-dropdown
            v-if="column.sortable"
            :id="`ips-table-sort-dropdown-${column.key}`"
            aria-describedby="TODO"
            class="column-headers"
          >
            <template slot="trigger">
              <a
                :id="`ips-table-sort-link-${column.key}`"
                :class="{ active: sorting.key === column.key }"
                aria-haspopup="true"
                href
                @click.prevent
              >
                <span
                  v-tooltip="{
                    title: column.label,
                    placement: 'bottom',
                    onlyWhenTruncated: true,
                    offset: [0, 4],
                  }"
                  >{{ column.label }}</span
                >
                <ips-icon
                  v-if="sorting.order"
                  :icon="getSortOrderIcon(sorting.order, column.key)"
                  size="sm"
                />
              </a>
            </template>
            <template #items="scope">
              <p class="dropdown-header text-start ps-3">SORT</p>

              <!-- sort ascending -->
              <a
                class="dropdown-item fw-normal"
                href
                :class="{ active: sorting.key === column.key && sorting.order === 'asc' }"
                @click.prevent="sortBy('asc', column, scope)"
              >
                <div class="dropdown-item-left">
                  <ips-icon
                    v-if="sorting.key === column.key && sorting.order === 'asc'"
                    icon="ico-tick"
                  ></ips-icon>
                </div>
                <div class="dropdown-item-label">Ascending</div>
              </a>

              <!-- sort descending -->
              <a
                class="dropdown-item fw-normal"
                href
                :class="{ active: sorting.key === column.key && sorting.order === 'desc' }"
                @click.prevent="sortBy('desc', column, scope)"
              >
                <div class="dropdown-item-left">
                  <ips-icon
                    v-if="sorting.key === column.key && sorting.order === 'desc'"
                    icon="ico-tick"
                  ></ips-icon>
                </div>
                <div class="dropdown-item-label">Descending</div>
              </a>
            </template>
          </ips-dropdown>

          <div v-if="!column.sortable">
            {{ column.label }}
          </div>
        </th>
        <th v-if="editable">&nbsp;</th>
        <th v-if="$scopedSlots.lastColumn"></th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="row in sortedRows"
        :key="row.id"
        class="table-row-hover"
        :class="{ 'table-active': row.checked }"
        :style="row.rowStyle"
        @click="emitEvent('row-click', { action: 'edit', data: row })"
        @mouseover="hoveredRowId = row.id"
        @mouseout="hoveredRowId = null"
      >
        <td v-if="selectable" class="ps-3 pe-0">
          <ips-checkbox
            :id="id.concat('selectRow').concat(row)"
            label="Select row"
            :checked="row.checked"
            :type="selectType"
            :control="selectType"
            :group="id.concat('Group')"
            @change="selectRow(row)"
          />
        </td>
        <td
          v-for="column in columns"
          :key="column.key"
          :class="columnClasses(column)"
          :style="column.style"
        >
          <div
            class="text-truncate max-lines"
            v-tooltip="{
              title: prepareData(column, row),
              placement: 'bottom',
              onlyWhenTruncated: true,
              offset: [0, 4],
            }"
          >
            <ips-icon v-if="column.showIcon" class="pe-3" :icon="getFileTypeIcon(column, row)" />
            <span>{{ prepareData(column, row) }}</span>
          </div>
        </td>
        <td v-if="editable">
          <ips-icon icon="ico-edit"></ips-icon>
        </td>
        <td v-if="$scopedSlots.lastColumn">
          <slot name="lastColumn" :row="row" :hovered="hoveredRowId === row.id"></slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import IpsIcon from '@ipscape/ips-icon';
import IpsCheckbox from '@ipscape/ips-checkbox';
import IpsDropdown from '@ipscape/ips-dropdown';
import { shortenText } from '../../../utils/utilities';
import tooltip from '../../../directives/tooltip';

export default {
  name: 'IpsTable',
  components: {
    IpsIcon,
    IpsCheckbox,
    IpsDropdown,
  },
  directives: {
    tooltip: tooltip,
  },
  props: {
    id: {
      name: 'id',
      description: 'The id of the table',
      type: String,
      default: 'ipsTable',
      required: true,
    },
    type: {
      name: 'type',
      type: String,
      default: 'hover',
      description: 'Whether table is striped or hover type',
      required: true,
      validator: (value) => ['bordered', 'striped', 'dark', 'hover'].indexOf(value) !== -1,
    },
    headType: {
      name: 'head-type',
      type: String,
      default: 'light',
      description:
        'Similar to tables and dark tables, use the modifier classes .thead-light or .thead-dark to make <thead> appear light or dark gray.',
      required: false,
      validator: (value) => ['dark', 'light'].indexOf(value) !== -1,
    },
    columns: {
      name: 'columns',
      type: Array,
      default: () => [],
      description: 'Table columns',
      required: true,
    },
    rows: {
      name: 'rows',
      type: Array,
      default: () => [],
      description: 'Table rows',
      required: true,
    },
    selectable: {
      name: 'selectable',
      type: Boolean,
      default: true,
      description: 'Make rows selectable with a select box',
      required: false,
    },
    selectAll: {
      name: 'selectAll',
      type: Boolean,
      default: false,
      description: 'Adds a checkbox in the table header to select all items',
      required: false,
    },
    selectType: {
      name: 'selectType',
      type: String,
      default: 'checkbox',
      description: 'Type of select component.',
      required: false,
      validator: (value) => ['checkbox', 'radio'].indexOf(value) !== -1,
    },
    editable: {
      name: 'editable',
      type: Boolean,
      default: false,
      description: 'Make rows editable',
      required: false,
    },
    pagination: {
      name: 'pagination',
      type: Object,
      default: () => {
        return {};
      },
      description: 'Pagination instructions',
      required: false,
    },
  },
  data() {
    return {
      status: false,
      indeterminate: false,
      hoveredRowId: null,
      sortedRows: this.rows,
      checked: 0,
      sorting: {
        key: null,
        order: 'desc',
      },
    };
  },
  computed: {
    tableClass() {
      return [this.type, `table-${this.type}`];
    },
  },
  watch: {
    rows: {
      handler(newVal) {
        this.sortedRows = newVal;
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateCheckedItems();
    });
  },
  methods: {
    prepareData(column, data) {
      let dataValue;
      if (column.type === 'array') {
        if (data[column.key].length > 0) {
          data[column.key].forEach((item) => {
            dataValue = dataValue ? (dataValue += `, ${item} \n`) : `${item} \n`;
          });
        }
        dataValue = '';
      } else if (column.type === 'boolean') {
        if (data[column.key]) {
          dataValue = data[column.key] === 1;
        }
      } else {
        if (column.maxLength) {
          dataValue = shortenText(data[column.key], column.maxLength);
        }
        dataValue = data[column.key];
      }

      // format data if formatter callback exists
      if (column.formatter) {
        return column.formatter(dataValue);
      } else {
        return dataValue;
      }
    },
    compare({ key = '', order = 'asc', dataType = 'text' }) {
      return (a, b) => {
        // eslint-disable-next-line no-prototype-builtins
        if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
          return 0;
        }

        let comparison = 0;

        // sort by date
        if (dataType === 'date') {
          comparison = new Date(a[key]) - new Date(b[key]);
        } else {
          const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key];
          const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key];

          if (varA > varB) {
            comparison = 1;
          } else if (varA < varB) {
            comparison = -1;
          }
        }
        return order === 'desc' ? comparison * -1 : comparison;
      };
    },
    sortBy(order, column, scope) {
      this.sorting = { key: column.key, order };
      this.sortedRows.sort(
        this.compare({
          key: column.sortKey ? column.sortKey : column.key,
          order,
          dataType: column.type,
        })
      );
      if (typeof scope.close === 'function') scope.close();
    },
    toggleSelectAll(newValue) {
      this.$nextTick(() => {
        this.toggleItems(newValue);
        this.calculateCheckedItems();
      });
    },
    toggleItems(value) {
      this.$nextTick(() => {
        this.rows.forEach((item) => {
          item.checked = value;
        });
      });
    },
    calculateIndeterminateState() {
      this.$nextTick(() => {
        this.indeterminate = this.checked > 0 && this.checked < this.rows.length;
      });
    },
    calculateCheckedItems() {
      this.$nextTick(() => {
        this.checked = 0;
        this.rows.forEach((item) => {
          if (item.checked) this.checked += 1;
        });
        if (this.checked === this.rows.length) this.status = true;
        if (this.checked === 0) this.status = false;
        this.calculateIndeterminateState();
      });
    },
    selectRow(data) {
      this.$nextTick(() => {
        if (this.selectType === 'checkbox') {
          this.rows.forEach((item) => {
            if (this.selectType === 'checkbox' && item.id === data.id) {
              item.checked = !item.checked;
              this.calculateCheckedItems();
              this.emitEvent('row-selected', { action: 'select', data: data });
            }
          });
        } else if (this.selectType === 'radio') {
          this.emitEvent('row-selected', { action: 'select', data: data });
        }
      });
    },
    emitEvent(event, data) {
      this.$emit(event, data.data);
    },
    getSortOrderIcon(sortOrder, columnKey) {
      if (this.sorting.key === columnKey) {
        return sortOrder === 'asc' ? 'ico-ascending' : 'ico-descending';
      } else {
        return 'ico-down';
      }
    },
    getFileTypeIcon(column, data) {
      switch (data.type) {
        case 'compressed': {
          return 'ico-zip-file';
        }
        case 'spreadsheet': {
          return 'ico-spreadsheet-file';
        }
        case 'image': {
          return 'ico-image-file';
        }
        case 'music': {
          return 'ico-music-file';
        }
        case 'presentation': {
          return 'ico-presentation-file';
        }
        case 'pdf': {
          return 'ico-pdf-file';
        }
        case 'video': {
          return 'ico-video-file';
        }
        case 'word': {
          return 'ico-word-file';
        }
        case 'other':
        default: {
          return 'ico-other-file';
        }
      }
    },
    columnHeaderClasses(column) {
      let columnHeaderClassesArray = [];
      if (this.headType) {
        columnHeaderClassesArray.push(`thead-${this.headType}`);
      }
      columnHeaderClassesArray.push(column.headerClasses);
      return columnHeaderClassesArray;
    },
    columnClasses(column) {
      let columnClassesArray = [];
      if (column.type === 'array') {
        columnClassesArray.push('pre-formatted');
      }
      columnClassesArray.push(column.classes);
      return columnClassesArray;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'IpsTable';
</style>
